import striptags from 'striptags';

import inViewport from '../inViewport';
import emitter from '../emitter';
import {
  getConfig,
  storage,
  type Storage,
  setEventTrackingConfig,
} from './config';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import htmlEntitiesDecode from '../../core/htmlEntitiesDecode';

// Note: we'll reuse Tools from Core once we remove Breakpoint alias from core
export interface MetaData {
  [index: string]: string | boolean | number;
}
export interface Tools {
  tracking?: {
    element?: string;
    methods?: string[];
    details?: string[];
    metaData?: MetaData;
  };
}

/**
 * This object maps the event type to 'dtm element key' based on
 * Analytics Team specs
 */
const TYPE_KEY_MAP = {
  click: 'click',
  render: 'impression',
  inViewport: 'impression',
  mouseEnter: 'click',
  mouseLeave: 'click',
};

let areDataAttrsEnabled = false;
export const enableDataAttributes = () => {
  areDataAttrsEnabled = true;
};
export const disableDataAttributes = () => {
  areDataAttrsEnabled = false;
};

const getKey = (
  {
    element,
    details,
  }: {
    element: string;
    details: string[];
  },
  type?: keyof typeof TYPE_KEY_MAP
): string => {
  const config = getConfig();
  const key = [];

  if (!config.ignore && type) {
    key.push(config.countryCode, config.moduleID, TYPE_KEY_MAP[type]);
  }

  key.push([element, ...details].filter(Boolean).join(':'));

  return key.filter(Boolean).join('>>');
};

const re = /([0-9]+|([A-Z][a-z]+)|[a-z]+|([A-Z]+)(?![a-z]))/g;
const kebabCase = (str: string) =>
  (str.match(re) || []).map((x) => x.toLowerCase()).join('-');

const cleanDetails = (details: string[]) =>
  details
    .map((str) => htmlEntitiesDecode(str || ''))
    .map((str) => striptags(str))
    .map((str) => str.replace(/[^-\w\s]/gi, ''))
    .map(kebabCase);

export const track = (fn: (data: any) => void) =>
  emitter.on(getConfig().eventName || '', fn);

export const stopTrack = (fn: (data: any) => void) =>
  emitter.off(getConfig().eventName || '', fn);

export const emit = (
  type: keyof typeof TYPE_KEY_MAP,
  data: {
    element: string;
    details: string[];
    metaData?: MetaData;
    props?: { [index: string]: any };
  }
) => {
  const config = getConfig();
  const key = getKey(
    {
      element: data.element,
      details: cleanDetails(data.details),
    },
    type
  );
  const dataWithKey = {
    key,
    ...data,
  };

  if ((storage as Storage).isDevMode) {
    console.log('EVENT', '::', type, dataWithKey);
  }

  emitter.emit(config.eventName || '', [type, dataWithKey]);
};

export interface DefaultEventTrackingProps {
  children?: React.ReactNode;
  onClick?: any;
  onMouseEnter?: any;
  onMouseLeave?: any;
  tools?: Tools;
  forwardedRef?: React.Ref<HTMLElement>;
  ['data-event-key']?: string;
  ['data-event-metadata']?: string;
  ['data-trackit-element']?: string;
  ['data-trackit-identifier']?: string;
  ['data-trackit-detail']?: string;
}

export function useEventTracking<Props extends DefaultEventTrackingProps>(
  props: Props,
  Component?: React.ComponentType<Props>,
  forwardedRef?: any
) {
  props = {
    ...((Component && Component.defaultProps) || {}),
    ...props,
  };
  const fallbackRef = useRef();
  const ref = forwardedRef || fallbackRef;
  const tools = props.tools || {};
  const tracking = tools.tracking || {};
  const methods = tracking.methods || [];
  const trackMouseEnter = methods.includes('mouseEnter');
  const trackMouseLeave = methods.includes('mouseLeave');
  const trackClick = methods.includes('click');
  const trackRender = methods.includes('render');
  const trackInViewport = methods.includes('inViewport');
  const eventData = useMemo(
    () => ({
      element:
        tracking.element ||
        (Component && Component.displayName) ||
        (Component && Component.name) ||
        'Unknown',
      details: cleanDetails(
        tracking.details ||
          (props.children && typeof props.children === 'string'
            ? [props.children.toString()]
            : [])
      ),
      metaData: tracking.metaData || {},
      props,
    }),
    [Component, props, tracking.details, tracking.element, tracking.metaData]
  );

  useEffect(() => {
    if (trackInViewport && ref.current) {
      const { disconnect } = inViewport(ref.current, ({ isVisible }) => {
        if (isVisible) {
          emit('inViewport', eventData);
          disconnect();
        }
      });
    }
    // THIS HOOK IS FOR IMPRESSION TRACKING. NEEDS REFACTOR...STOPGAP IS AN EMPTY DEP ARRAY
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = trackClick
    ? (e: Event) => {
        emit('click', eventData);

        if (props.onClick) {
          props.onClick(e);
        }
      }
    : props.onClick;

  if (trackRender) {
    emit('render', eventData);
  }

  const key = getKey({
    element: eventData.element,
    details: eventData.details,
  });

  const onMouseEnter = trackMouseEnter
    ? (e: Event) => {
        emit('mouseEnter', eventData);

        if (props.onMouseEnter) {
          props.onMouseEnter(e);
        }
      }
    : props.onMouseEnter;

  const onMouseLeave = trackMouseLeave
    ? (e: Event) => {
        emit('mouseLeave', eventData);

        if (props.onMouseLeave) {
          props.onMouseLeave(e);
        }
      }
    : props.onMouseLeave;

  return {
    ref,
    props: areDataAttrsEnabled
      ? {
          ...props,
          ['data-event-key']: key,
          ['data-event-metadata']: JSON.stringify(eventData.metaData),
          ['data-trackit-element']: eventData.element,
          ['data-trackit-detail']: eventData.details.filter(Boolean).join('-'),
        }
      : props,
    onClick,
    onMouseEnter,
    onMouseLeave,
  };
}

function withEventTracking<P extends DefaultEventTrackingProps>(
  Component: React.ComponentType<P>
) {
  const WrapperComponent = forwardRef((passedProps: P, forwardedRef?: any) => {
    const { onMouseEnter, onMouseLeave, onClick, props, ref } =
      useEventTracking(passedProps, Component, forwardedRef);

    const { tools: _tools, ...propsForElement } = props;
    return (
      // @ts-ignore
      <Component
        {...propsForElement}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        forwardedRef={ref}
        key={props.tools?.tracking?.element}
      />
    );
  });

  WrapperComponent.displayName = `withEventTracking(${
    Component.displayName || Component.name
  })`;

  return WrapperComponent;
}

interface EventTrackingProps {
  children: React.FunctionComponent<{
    onClick?: () => void;
    ref?: React.RefObject<unknown>;
  }>;
  tools: Tools;
}

export const EventTracking = ({ children, tools }: EventTrackingProps) => {
  const { onClick, ref } = useEventTracking<{ tools: Tools }>({ tools });

  return children({
    onClick,
    ref,
  });
};

export { setEventTrackingConfig };

export default withEventTracking;
